import { buildUserTopic, REALTIME_SUBSCRIBE_LEAF, type RealtimeMessage } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { notification } from '@/notifications/notification-store'
import { isRealtimeConfigured, realtimeConfig } from '@/realtime/realtime-config'
import { subscribeRealtime, useRealtimeConnectionState } from '@/realtime/realtime-dispatcher'

/**
 * Dev-only diagnostics for the realtime channel (ADR-0020).
 *
 * Exists because every failure mode of this channel is silent from the outside:
 * a missing build variable, a topic the authorizer will not grant, and a healthy
 * connection that receives nothing all present as "nothing happens". This page
 * shows the resolved configuration, the live connection state, the exact topic
 * subscribed to, and every message as it arrives.
 */

/** Renders a resolved value, or a clearly-marked gap. */
function ConfigRow({ label, value }: { label: string; value: string | undefined }) {
	return (
		<div className="flex flex-col gap-ds-1 border-b border-ds-border py-ds-2 last:border-b-0">
			<span className="text-ds-xs text-ds-text-muted uppercase">{label}</span>
			{value ? (
				<code className="break-all font-ds-mono text-ds-sm text-ds-text">{value}</code>
			) : (
				<span className="text-ds-sm text-ds-danger">missing</span>
			)}
		</div>
	)
}

/** Buttons that raise each notification variant, so the status bar can be seen. */
function NotificationProbes() {
	return (
		<SectionCard
			title="Notification status bar"
			description="The bar is mounted app-wide but renders nothing until something is raised."
			className="bg-ds-surface p-ds-4"
		>
			<div className="flex flex-wrap gap-ds-2">
				<Button variant="muted" onPress={() => notification.info('Informational notification.')}>
					Info
				</Button>
				<Button variant="success" onPress={() => notification.success('Success notification.')}>
					Success
				</Button>
				<Button variant="secondary" onPress={() => notification.warning('Warning notification.')}>
					Warning
				</Button>
				<Button variant="danger" onPress={() => notification.error('Error notification with no action.')}>
					Error
				</Button>
				<Button
					variant="danger"
					onPress={() =>
						notification.error('Error notification with an action.', {
							action: { label: 'Retry', onPress: () => notification.info('Action invoked.') },
						})
					}
				>
					Error + action
				</Button>
			</div>
		</SectionCard>
	)
}

/** Messages delivered to this session, newest first. */
function DeliveredMessages({
	messages,
	userId,
}: {
	messages: { received: string; message: RealtimeMessage }[]
	userId: string | undefined
}) {
	return (
		<SectionCard title="Delivered messages" count={messages.length} className="bg-ds-surface p-ds-4">
			{messages.length === 0 ? (
				<p className="text-ds-sm text-ds-text-muted">
					Nothing yet. Publish one with{' '}
					<code className="font-ds-mono text-ds-xs">
						npx sst shell --stage &lt;stage&gt; -- npx tsx scripts/src/local/emit-realtime-test-event.ts{' '}
						{userId ?? '<userId>'}
					</code>
				</p>
			) : (
				<ul className="flex flex-col gap-ds-2">
					{messages.map((entry) => (
						<li
							key={`${entry.received}-${entry.message.entityRef.id}`}
							className="rounded-ds-base border border-ds-border p-ds-2"
						>
							<div className="flex flex-wrap items-baseline gap-ds-2">
								<code className="font-ds-mono text-ds-sm text-ds-text">{entry.message.type}</code>
								<span className="text-ds-xs text-ds-text-muted">{entry.received}</span>
							</div>
							<code className="break-all font-ds-mono text-ds-xs text-ds-text-muted">
								{entry.message.entityRef.type}:{entry.message.entityRef.id}
							</code>
						</li>
					))}
				</ul>
			)}
		</SectionCard>
	)
}

export function RealtimeDiagnosticsPage() {
	const { user } = useAuth()
	const connectionState = useRealtimeConnectionState()
	const [messages, setMessages] = useState<{ received: string; message: RealtimeMessage }[]>([])

	// An unfiltered subscription: this page reports what the channel delivers, so
	// narrowing it here would hide exactly the messages worth seeing.
	useEffect(
		() =>
			subscribeRealtime({}, (message) => {
				setMessages((current) => [{ received: new Date().toISOString(), message }, ...current].slice(0, 20))
			}),
		[],
	)

	// The topic the connection actually subscribes to, rebuilt through the same
	// helper the authorizer grants with, so a mismatch is visible rather than
	// inferred.
	const topic =
		realtimeConfig && user
			? buildUserTopic({
					app: realtimeConfig.topicPrefix.split('/')[0] ?? '',
					stage: realtimeConfig.topicPrefix.split('/')[1] ?? '',
					userId: user.userId,
					entityType: REALTIME_SUBSCRIBE_LEAF,
				})
			: undefined

	return (
		<div className="flex flex-col gap-ds-6 p-ds-6">
			<header className="flex flex-col gap-ds-1">
				<h1 className="font-ds-display text-ds-2xl text-ds-text">Realtime diagnostics</h1>
				<p className="text-ds-sm text-ds-text-muted">
					Build-time configuration, live connection state, and delivered messages.
				</p>
			</header>

			<SectionCard title="Connection" className="bg-ds-surface p-ds-4">
				<div className="flex items-center gap-ds-2 pb-ds-3">
					<span
						aria-hidden
						className={`inline-block size-3 rounded-ds-full ${
							connectionState === 'connected'
								? 'bg-ds-success'
								: connectionState === 'reconnecting'
									? 'bg-ds-warning'
									: 'bg-ds-danger'
						}`}
					/>
					<span className="text-ds-sm text-ds-text">{connectionState}</span>
				</div>
				<ConfigRow label="Subscribed topic" value={topic} />
				<ConfigRow label="Atlas user id (topic subject)" value={user?.userId} />
			</SectionCard>

			<SectionCard
				title={`Build variables ${isRealtimeConfigured ? '' : '— incomplete, channel disabled'}`}
				className="bg-ds-surface p-ds-4"
			>
				<ConfigRow label="VITE_REALTIME_ENDPOINT" value={realtimeConfig?.endpoint} />
				<ConfigRow label="VITE_REALTIME_AUTHORIZER" value={realtimeConfig?.authorizer} />
				<ConfigRow label="VITE_REALTIME_TOPIC_PREFIX" value={realtimeConfig?.topicPrefix} />
			</SectionCard>

			<NotificationProbes />

			<DeliveredMessages messages={messages} userId={user?.userId} />
		</div>
	)
}
